import React, { useState, useEffect, useCallback } from 'react';
import { useGlobalContext } from '../../contexts/GlobalContext';
import ApiServiceV2 from '../../services/ApiServiceV2';
import { routes } from '../../hooks/routes';
import Heading from '../../components/Heading';
import Button from '../../components/Button';
import styles from './ContactFormOverview.module.scss';
import cx from 'classnames';
import CreateFormModal from "./CreateFormModal";
import DeleteFormModal from "./DeleteFormModal";
import EditFormNameModal from "./EditFormNameModal";
import ContactFormListView from "./ContactFormListView";
import { ContactForm } from '../../types/ContactForm';

type FormListItem = {
    id: string;
    name: string;
    active: boolean;
    created_at?: string | null;
    config?: ContactForm['config'];
};

const ContactFormOverview: React.FC = () => {
    const { showToast, showModal, hideModal } = useGlobalContext();
    const [forms, setForms] = useState<FormListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);


    const loadForms = useCallback(async () => {
        try {
            setIsLoading(true);
            const api = new ApiServiceV2();
            const response = await api.callApi(routes.getFormsList);
            
            if (response.data?.data?.forms) {
                setForms(response.data.data.forms);
            }
        } catch (error) {
            console.error('Failed to load forms:', error);
            showToast('top-right', 'error', 'Fehler beim Laden der Formulare');
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        loadForms();
    }, []);


    const handleCreateForm = useCallback(async (name: string) => {
        try {
            const api = new ApiServiceV2();
            const response = await api.callApi(routes.createForm, 'post', { name });
            
            if (response.data?.data?.form) {
                const newForm: FormListItem = {
                    id: response.data.data.form.id,
                    name: response.data.data.form.name,
                    active: response.data.data.form.active,
                    created_at: response.data.data.form.created_at
                };
                setForms(prev => [...prev, newForm]);
                showToast('top-right', 'success', 'Formular erfolgreich erstellt!');
                hideModal();
            }
        } catch (error) {
            console.error('Failed to create form:', error);
            showToast('top-right', 'error', 'Fehler beim Erstellen des Formulars');
        }
    }, [showToast, hideModal]);


    const handleDeleteForm = useCallback(async (formId: string) => {
        try {
            const api = new ApiServiceV2();
            await api.callApi(routes.deleteForm.replace(':id', formId), 'delete');
            
            setForms(prev => prev.filter(form => form.id !== formId));
            showToast('top-right', 'success', 'Formular erfolgreich gelöscht!');
            hideModal();
        } catch (error) {
            console.error('Failed to delete form:', error);
            showToast('top-right', 'error', 'Fehler beim Löschen des Formulars');
        }
    }, [showToast, hideModal]);


    const handleDuplicateForm = useCallback(async (formId: string) => {
        try {
            const api = new ApiServiceV2();
            const response = await api.callApi(routes.duplicateForm.replace(':id', formId), 'post');
            
            if (response.data?.data?.form) {
                const duplicatedForm: FormListItem = {
                    id: response.data.data.form.id,
                    name: response.data.data.form.name,
                    active: response.data.data.form.active,
                    created_at: response.data.data.form.created_at
                };
                setForms(prev => [...prev, duplicatedForm]);
                showToast('top-right', 'success', 'Formular erfolgreich dupliziert!');
            }
        } catch (error) {
            console.error('Failed to duplicate form:', error);
            showToast('top-right', 'error', 'Fehler beim Duplizieren des Formulars');
        }
    }, [showToast]);

    const handleToggleVisibility = useCallback(async (formId: string) => {
        try {
            const api = new ApiServiceV2();
            await api.callApi(routes.toggleFormVisibility.replace(':id', formId), 'post');
            
            setForms(prev => prev.map(form => 
                form.id === formId 
                    ? { ...form, active: !form.active }
                    : form
            ));
            showToast('top-right', 'success', 'Sichtbarkeit erfolgreich geändert!');
        } catch (error) {
            console.error('Failed to toggle visibility:', error);
            showToast('top-right', 'error', 'Fehler beim Ändern der Sichtbarkeit');
        }
    }, [showToast]);

    const handleEditFormName = useCallback(async (formId: string, newName: string) => {
        try {
            const api = new ApiServiceV2();
            const response = await api.callApi(routes.updateForm.replace(':id', formId), 'post', { 
                name: newName 
            });
            
            if (response.data?.data?.success !== false) {
                setForms(prev => prev.map(form => 
                    form.id === formId 
                        ? { ...form, name: newName }
                        : form
                ));
                showToast('top-right', 'success', 'Formularname erfolgreich geändert!');
                hideModal();
            }
        } catch (error) {
            console.error('Failed to update form name:', error);
            showToast('top-right', 'error', 'Fehler beim Ändern des Formularnamens');
        }
    }, [showToast, hideModal]);

    const openCreateModal = useCallback(() => {
        showModal({
            content: (
                <CreateFormModal
                    onConfirm={handleCreateForm}
                    onCancel={hideModal}
                />
            ),
            fullscreen: false,
            size: 'sm'
        });
    }, [showModal, handleCreateForm, hideModal]);

    const openDeleteModal = useCallback((formId: string, formName: string) => {
        showModal({
            content: (
                <DeleteFormModal
                    formId={formId}
                    formName={formName}
                    onConfirm={handleDeleteForm}
                    onCancel={hideModal}
                />
            ),
            fullscreen: false,
            size: 'sm'
        });
    }, [showModal, handleDeleteForm, hideModal]);

    const openEditModal = useCallback((formId: string, formName: string) => {
        showModal({
            content: (
                <EditFormNameModal
                    formId={formId}
                    currentName={formName}
                    onConfirm={handleEditFormName}
                    onCancel={hideModal}
                />
            ),
            fullscreen: false,
            size: 'sm'
        });
    }, [showModal, handleEditFormName, hideModal]);

    if (isLoading) {
        return (
            <div className={cx(styles['loading-container'])}>
                <div>Formulare werden geladen...</div>
            </div>
        );
    }

    return (
        <div className={cx(styles['overview-container'])}>
            <div className={cx(styles['overview-header'])}>
                <Heading level={2} classes={cx(styles['overview-title'])}>
                    Kontaktformulare
                </Heading>
                <Button
                    hierarchy="primary"
                    fn={openCreateModal}
                    text="Neues Formular erstellen"
                />
            </div>

            {forms.length === 0 ? (
                <div className={cx(styles['empty-state'])}>
                    <div className={cx(styles['empty-message'])}>
                        Noch keine Formulare vorhanden.
                    </div>
                    <Button
                        hierarchy="primary"
                        fn={openCreateModal}
                        text="Erstes Formular erstellen"
                        classes={cx(styles['empty-create-button'])}
                    />
                </div>
            ) : (
                <ContactFormListView
                    forms={forms}
                    onDuplicate={handleDuplicateForm}
                    onDelete={openDeleteModal}
                    onToggleVisibility={handleToggleVisibility}
                    onEditName={openEditModal}
                />
            )}
        </div>
    );
};

export default ContactFormOverview; 